import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { NavigationService } from '../../services/navigation.service';
import { PermissionsService } from '../../services/permissions.service';
import { AuthService } from '../../services/auth.service';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface NavSubItem {
  label: string;
  route: string;
  disabled?: boolean;
  module?: string;
  level?: 'read' | 'write';
}

export interface NavItem {
  label: string;
  icon: string;
  route: string;
  badge?: number;
  disabled?: boolean;
  submenu?: NavSubItem[];
  module?: string;
}

@Component({
  selector: 'app-site-panel-nav',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule
  ],
  template: `
    <div class="ds-sidebar" [class.collapsed]="collapsed" [class.open]="isOpen">
      
      <!-- Botón Colapsar (Solo Desktop) — migrado from webapp toggleSidebar -->
      <div class="toggleSidebar" [class.close]="!collapsed" (click)="toggleCollapse()" *ngIf="!isMobile()">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M-8.58275e-07 12C-2.78888e-07 5.37258 5.37258 -6.74786e-07 12 -9.5399e-08C18.6274 4.83988e-07 24 5.37258 24 12C24 18.6274 18.6274 24 12 24C5.37258 24 -1.43766e-06 18.6274 -8.58275e-07 12Z" fill="#066BF1"/>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M9.29289 18.7071C8.90237 18.3166 8.90237 17.6834 9.29289 17.2929L14.5858 12L9.29289 6.70711C8.90237 6.31658 8.90237 5.68342 9.29289 5.29289C9.68342 4.90237 10.3166 4.90237 10.7071 5.29289L16.7071 11.2929C17.0976 11.6834 17.0976 12.3166 16.7071 12.7071L10.7071 18.7071C10.3166 19.0976 9.68342 19.0976 9.29289 18.7071Z" fill="white"/>
        </svg>
      </div>

      <div class="sidebar-scrollable" (scroll)="closeFlyout()">
        <nav class="nav-menu">
          <ng-container *ngFor="let item of filteredNavItems">
            
            <!-- Item con Submenú -->
            <div *ngIf="item.submenu"
                 class="nav-group"
                 [class.expanded]="isExpanded(item)"
                 [class.flyout-open]="flyoutItem === item"
                 (mouseenter)="onGroupEnter(item, $event)"
                 (mouseleave)="scheduleFlyoutClose()">
              <div class="nav-link" (click)="toggleExpand(item)" [class.active]="isParentActive(item)">
                <div class="link-icon-box">
                  <mat-icon>{{ item.icon }}</mat-icon>
                </div>
                <span class="link-text" *ngIf="!collapsed">{{ item.label }}</span>
                <mat-icon class="expand-arrow" *ngIf="!collapsed">expand_more</mat-icon>
                
                <!-- Badge de grupo -->
                <div class="nav-badge" *ngIf="item.badge && !collapsed">
                  {{ item.badge > 99 ? '99+' : item.badge }}
                </div>
              </div>

              <!-- Submenú Animado -->
              <div class="submenu" *ngIf="!collapsed">
                <a *ngFor="let sub of item.submenu"
                   [routerLink]="sub.route"
                   routerLinkActive="active"
                   class="submenu-link"
                   (click)="onSubItemClick(sub)">
                  <span class="submenu-dot"></span>
                  <span class="submenu-text">{{ sub.label }}</span>
                </a>
              </div>
            </div>

            <!-- Item Regular -->
            <a *ngIf="!item.submenu"
               [routerLink]="item.route"
               routerLinkActive="active"
               class="nav-link"
               (click)="onNavItemClick(item)">
              <div class="link-icon-box">
                <mat-icon>{{ item.icon }}</mat-icon>
              </div>
              <span class="link-text" *ngIf="!collapsed">{{ item.label }}</span>
              <div class="nav-badge" *ngIf="item.badge && !collapsed">
                {{ item.badge > 99 ? '99+' : item.badge }}
              </div>
            </a>

          </ng-container>
        </nav>
      </div>

      <!-- Flyout de submenú (sidebar colapsado, hover) -->
      <div class="nav-flyout"
           *ngIf="flyoutItem"
           [style.top.px]="flyoutTop"
           [style.left.px]="flyoutLeft"
           (mouseenter)="cancelFlyoutClose()"
           (mouseleave)="scheduleFlyoutClose()">
        <div class="nav-flyout-title">{{ flyoutItem.label }}</div>
        <a *ngFor="let sub of flyoutItem.submenu"
           [routerLink]="sub.route"
           routerLinkActive="active"
           class="nav-flyout-link"
           (click)="onFlyoutSubItemClick(sub)">
          {{ sub.label }}
        </a>
      </div>

      <!-- Footer del Sidebar -->
      <div class="sidebar-footer" *ngIf="!collapsed">
        <div class="footer-info">
          <span class="app-version">BDL v1.2.0</span>
          <span class="app-env">Production</span>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; height: 100%; }

    .ds-sidebar {
      width: 240px; height: 100%; background: #fff; border-right: 1px solid #e2e8f0;
      display: flex; flex-direction: column; position: relative; transition: width .3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .ds-sidebar.collapsed { width: 72px; }

    .sidebar-scrollable { flex: 1; overflow-y: auto; overflow-x: hidden; padding: 12px 0; }
    .sidebar-scrollable::-webkit-scrollbar { width: 4px; }
    .sidebar-scrollable::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }

    /* migrated from webapp .toggleSidebar (styles.scss L20361) */
    .toggleSidebar {
      position: absolute;
      z-index: 10;
      top: 16px;
      right: -12px;
      width: 24px;
      height: 24px;
      transition: transform 0.3s ease-in-out;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .toggleSidebar.close { transform: rotate(180deg); }
    .toggleSidebar svg { display: block; width: 24px; height: 24px; }

    .nav-menu { display: flex; flex-direction: column; gap: 2px; }

    .nav-link {
      display: flex; align-items: center; padding: 10px 16px;
      color: #64748b; text-decoration: none; cursor: pointer; transition: all .2s;
      position: relative; border-left: 3px solid transparent;
    }
    .nav-link:hover { color: #066bf1; background: #f8fafc; .link-icon-box mat-icon { color: #066bf1; } }
    .nav-link.active { 
      color: #066bf1; font-weight: 600; border-left-color: #066bf1; background: #f8fafc;
      .link-icon-box mat-icon { color: #066bf1; } 
    }

    .link-icon-box {
      width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;
      mat-icon { font-size: 20px; width: 20px; height: 20px; color: #64748b; transition: color .2s; }
    }

    .link-text { margin-left: 12px; font-size: 14px; flex: 1; white-space: nowrap; }

    .expand-arrow { font-size: 18px; width: 18px; height: 18px; color: #94a3b8; transition: transform .2s; }
    .expanded .expand-arrow { transform: rotate(180deg); color: #066bf1; }

    .nav-badge {
      background: #dc2626; color: #fff; font-size: 10px; font-weight: 700;
      padding: 2px 6px; border-radius: 10px; min-width: 18px; text-align: center;
    }

    /* Submenu */
    .submenu {
      display: none; flex-direction: column; margin-top: 2px; margin-bottom: 8px;
    }
    .expanded .submenu { display: flex; }

    .submenu-link {
      display: flex; align-items: center; padding: 8px 16px 8px 46px;
      color: #64748b; text-decoration: none; font-size: 13px; transition: all .15s;
      position: relative; border-left: 3px solid transparent;
    }
    .submenu-link:hover { color: #066bf1; background: #f8fafc; }
    .submenu-link.active { color: #066bf1; font-weight: 600; background: #f8fafc; border-left-color: #066bf1; }

    .submenu-dot { display: none; } /* Removed dots/asterisks completely to match Rindegastos flat look */

    /* Flyout (sidebar colapsado) */
    .collapsed .nav-group.flyout-open > .nav-link {
      color: #066bf1; background: #f8fafc;
      .link-icon-box mat-icon { color: #066bf1; }
    }

    .nav-flyout {
      position: fixed; z-index: 1100; width: 188px; box-sizing: border-box;
      background: #fff; border-radius: 12px; padding: 16px 12px;
      box-shadow: 0 4px 20px rgba(2, 39, 89, 0.12), 0 0 0 1px rgba(226, 232, 240, 0.6);
      display: flex; flex-direction: column; gap: 4px;
      animation: flyoutIn .12s ease-out;
    }
    @keyframes flyoutIn { from { opacity: 0; transform: translateX(-4px); } to { opacity: 1; transform: none; } }

    .nav-flyout-title {
      font-size: 13px; font-weight: 600; color: #64748b; padding: 0 8px 8px; white-space: nowrap;
    }

    .nav-flyout-link {
      display: block; padding: 8px 12px; border-radius: 8px;
      color: #022759; text-decoration: none; font-size: 14px; white-space: nowrap;
      overflow: hidden; text-overflow: ellipsis; transition: background .15s, color .15s;
    }
    .nav-flyout-link:hover { background: #f8fafc; color: #066bf1; }
    .nav-flyout-link.active { background: #e6f0fe; color: #066bf1; font-weight: 600; }

    .sidebar-footer { padding: 16px 24px; border-top: 1px solid #f1f5f9; }
    .footer-info { display: flex; flex-direction: column; gap: 2px; }
    .app-version { font-size: 11px; font-weight: 600; color: #475569; }
    .app-env { font-size: 10px; color: #94a3b8; text-transform: uppercase; letter-spacing: .05em; }

    /* Mobile handling handled by app.component layout */
  `]
})
export class SitePanelNavComponent implements OnInit, OnDestroy {
  @Input() collapsed = false;
  @Input() isOpen = false;
  @Output() navItemClick = new EventEmitter<NavItem>();

  private destroy$ = new Subject<void>();
  expandedItems = new Set<NavItem>();
  filteredNavItems: NavItem[] = [];

  flyoutItem: NavItem | null = null;
  flyoutTop = 0;
  flyoutLeft = 0;
  private flyoutCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly FLYOUT_GAP = 8;
  private static readonly FLYOUT_CLOSE_DELAY = 150;

  private allNavItems: NavItem[] = [
    { label: 'Dashboard', icon: 'dashboard', route: '/dashboard' },
    {
      label: 'Empresas',
      icon: 'business',
      route: '',
      module: 'companies',
      submenu: [
        { label: 'Listado de Empresas', route: '/companies', module: 'companies' },
        { label: 'Empresas Funnel', route: '/companies/funnel', module: 'companies' },
        { label: 'Empresas Trial', route: '/companies/trial', module: 'companies' },
        { label: 'Logs de Usuarios', route: '/companies/usuarios-log', module: 'companies' }
      ]
    },
    { label: 'Usuarios', icon: 'people', route: '/users', module: 'users' },
    { label: 'Logs de Auditoría', icon: 'description', route: '/logs', module: 'logs' },
    {
      label: 'Facturación',
      icon: 'receipt',
      route: '',
      module: 'bills',
      submenu: [
        { label: 'Listado de Facturas', route: '/bills/list', module: 'bills' },
        { label: 'Nueva Factura', route: '/bills/new', module: 'bills' },
        { label: 'Pre-facturación', route: '/bills/prebills', module: 'bills' },
        { label: 'Prebill Argentina', route: '/bills/prebills-argentina', module: 'bills' },
        { label: 'Simulación PreBill', route: '/bills/preprebill-simulation', module: 'bills' },
        { label: 'Cobranzas', route: '/bills/collections', module: 'bills' },
        { label: 'Recaudado', route: '/bills/collected', module: 'bills' },
        { label: 'Comisiones KAM', route: '/bills/commissions', module: 'bills' }
      ]
    },
    {
      label: 'Conciliación',
      icon: 'compare_arrows',
      route: '',
      module: 'reconciliation',
      submenu: [
        { label: 'Resumen General', route: '/reconciliation/summary', module: 'reconciliation' },
        { label: 'Pagos Kushki', route: '/reconciliation/kushki', module: 'reconciliation' },
        { label: 'Pagos MercadoPago', route: '/reconciliation/mercadopago', module: 'reconciliation' },
        { label: 'Pagos Rebill', route: '/reconciliation/rebill', module: 'reconciliation' },
        { label: 'Gestión Supercartola', route: '/reconciliation/supercartola', module: 'reconciliation' },
        { label: 'Gestión de Balances', route: '/reconciliation/balances', module: 'reconciliation' },
        { label: 'Montos Incompletos', route: '/reconciliation/incomplete-amounts', module: 'reconciliation' }
      ]
    },
    {
      label: 'NPS',
      icon: 'reviews',
      route: '',
      submenu: [
        { label: 'Dashboard NPS', route: '/nps' },
        { label: 'NPS por Empresa', route: '/nps/companies' },
        { label: 'Feedback', route: '/nps/feedback' }
      ]
    },
    {
      label: 'Reportes',
      icon: 'assessment',
      route: '',
      module: 'reports',
      submenu: [
        { label: 'Informes de Gestión', route: '/informes', module: 'reports' },
        { label: 'Reportes Detallados', route: '/reportes', module: 'reports' },
        { label: 'Métricas Empresas', route: '/empresas', module: 'reports' },
        { label: 'Métricas Usuarios', route: '/usuarios', module: 'reports' }
      ]
    },
    {
      label: 'Soporte',
      icon: 'support_agent',
      route: '',
      module: 'support-tickets',
      submenu: [
        { label: 'Tickets de Soporte', route: '/support-tickets', module: 'support-tickets' },
        { label: 'Asistente IA', route: '/support-tickets/chat', module: 'support-tickets' },
        { label: 'Editor de Tablas', route: '/support-tickets/table-editor', module: 'support-tickets' }
      ]
    },
    {
      label: 'Administración',
      icon: 'admin_panel_settings',
      route: '',
      module: 'admin',
      submenu: [
        { label: 'Panel de Control', route: '/admin', module: 'admin' },
        { label: 'Configuración Global', route: '/configuracion', module: 'admin' },
        { label: 'Variables de Sistema', route: '/variables', module: 'admin' },
        { label: 'Control de Acceso', route: '/access', module: 'admin' },
        { label: 'Permisos Administrativos', route: '/permissions', module: 'permissions' },
        { label: 'Gestión de Emails', route: '/emails', module: 'admin' },
        { label: 'Marketing Email', route: '/email-mkt', module: 'admin' },
        { label: 'Políticas', route: '/politicas', module: 'admin' },
        { label: 'Bulk Upload', route: '/bulk-upload', module: 'admin' }
      ]
    },
    {
      label: 'Integraciones',
      icon: 'hub',
      route: '',
      // Clave propia: el fallback temporal la excluye. Quien tiene `admin` sigue entrando (alias en PermissionsService).
      module: 'integrations',
      submenu: [
        { label: 'Servicios', route: '/integrations-catalog/services', module: 'integrations' },
        { label: 'Solicitar servicio', route: '/integrations-catalog/onboarding', module: 'integrations', level: 'write' },
        { label: 'Solicitudes', route: '/integrations-catalog/requests', module: 'integrations', level: 'write' },
        { label: 'Monitoreo', route: '/integrations-catalog/monitoring', module: 'integrations', level: 'write' },
        { label: 'Documentación', route: '/integrations-catalog/docs', module: 'integrations' }
      ]
    },
    {
      label: 'Infraestructura',
      icon: 'monitor_heart',
      route: '',
      module: 'infra',
      submenu: [
        { label: 'Métricas RDS', route: '/infra', module: 'infra' },
        { label: 'Estado MongoDB', route: '/infra/mongo', module: 'infra' },
        { label: 'Alertas', route: '/infra/alerts', module: 'infra' },
        { label: 'Costos', route: '/infra/cost', module: 'infra' },
        { label: 'Logs de Diagnóstico', route: '/infra/diagnostic-logs', module: 'infra' },
        { label: 'Errores Front', route: '/infra/frontend-errors', module: 'infra' }
      ]
    },
    { label: 'Widget Rendidor', icon: 'widgets', route: '/widget-rendidor', module: 'admin' }
  ];

  constructor(
    private navigationService: NavigationService,
    private permissionsService: PermissionsService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.initializeNavigation();
    this.initializePermissionsFiltering();
  }

  private initializePermissionsFiltering(): void {
    combineLatest([
      this.permissionsService.permissions$,
      this.authService.currentUser$
    ]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.filterNavItems();
    });
    this.filterNavItems();
  }

  private filterNavItems(): void {
    const user = this.authService.getCurrentUser();
    const isRindegastosUser = this.permissionsService.isRindegastosUser();

    if (!isRindegastosUser || !user) {
      // Usuarios no-rindegastos con permisos (o aún sin cargar) conservan el menú completo.
      // Sin ningún permiso cargado, aplica el mismo fallback que el resto.
      if (user && this.permissionsService.isTemporaryDefaultAccessActive()) {
        this.filteredNavItems = this.filterByModuleAccess(this.allNavItems);
        return;
      }
      this.filteredNavItems = this.allNavItems;
      return;
    }

    // Si todavía no cargaron, mostramos todo para evitar un menú vacío momentáneo.
    if (!this.permissionsService.arePermissionsLoaded()) {
      this.filteredNavItems = this.allNavItems;
      return;
    }

    // Con cero permisos, hasReadPermission aplica el fallback temporal
    // (todo excepto soporte, integraciones e infraestructura).
    this.filteredNavItems = this.filterByModuleAccess(this.allNavItems);
  }

  private filterByModuleAccess(items: NavItem[]): NavItem[] {
    return items
      .map(item => {
        if (!item.module) return item;

        if (item.submenu) {
          const filteredSubmenu = item.submenu.filter(subItem => {
            const moduleToCheck = subItem.module || item.module;
            if (!moduleToCheck) return true;
            return subItem.level === 'write'
              ? this.permissionsService.hasWritePermission(moduleToCheck)
              : this.permissionsService.hasReadPermission(moduleToCheck);
          });
          if (filteredSubmenu.length === 0) return null;
          return { ...item, submenu: filteredSubmenu };
        }

        if (!this.permissionsService.hasReadPermission(item.module)) return null;
        return item;
      })
      .filter(item => item !== null) as NavItem[];
  }

  ngOnDestroy(): void {
    this.cancelFlyoutClose();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeNavigation(): void {
    this.navigationService.isOpen$.pipe(takeUntil(this.destroy$)).subscribe(isOpen => this.isOpen = isOpen);
    this.navigationService.isCollapsed$.pipe(takeUntil(this.destroy$)).subscribe(isCollapsed => {
      this.collapsed = isCollapsed;
      this.closeFlyout();
    });
  }

  onGroupEnter(item: NavItem, event: MouseEvent): void {
    if (!this.collapsed || this.isMobile()) return;
    this.cancelFlyoutClose();
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const estimatedHeight = 56 + (item.submenu?.length ?? 0) * 40;
    const maxTop = window.innerHeight - estimatedHeight - SitePanelNavComponent.FLYOUT_GAP;
    this.flyoutTop = Math.max(SitePanelNavComponent.FLYOUT_GAP, Math.min(rect.top, maxTop));
    this.flyoutLeft = rect.right + SitePanelNavComponent.FLYOUT_GAP;
    this.flyoutItem = item;
  }

  scheduleFlyoutClose(): void {
    this.cancelFlyoutClose();
    this.flyoutCloseTimer = setTimeout(() => this.closeFlyout(), SitePanelNavComponent.FLYOUT_CLOSE_DELAY);
  }

  cancelFlyoutClose(): void {
    if (this.flyoutCloseTimer !== null) {
      clearTimeout(this.flyoutCloseTimer);
      this.flyoutCloseTimer = null;
    }
  }

  closeFlyout(): void {
    this.cancelFlyoutClose();
    this.flyoutItem = null;
  }

  onFlyoutSubItemClick(subItem: NavSubItem): void {
    this.closeFlyout();
    this.onSubItemClick(subItem);
  }

  @HostListener('window:resize')
  onViewportChange(): void { this.closeFlyout(); }

  toggleCollapse(): void { this.navigationService.toggleCollapsed(); }

  toggleExpand(item: NavItem): void {
    if (this.expandedItems.has(item)) this.expandedItems.delete(item);
    else this.expandedItems.add(item);
  }

  isExpanded(item: NavItem): boolean { return this.expandedItems.has(item); }
  
  isParentActive(item: NavItem): boolean {
    if (!item.submenu) return false;
    return item.submenu.some(sub => window.location.pathname.includes(sub.route));
  }

  onNavItemClick(item: NavItem): void {
    this.navItemClick.emit(item);
    if (this.isMobile()) this.navigationService.close();
  }

  onSubItemClick(subItem: NavSubItem): void {
    if (this.isMobile()) this.navigationService.close();
  }

  isMobile(): boolean { return window.innerWidth <= 768; }
}
